import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, ApiRequestError } from "@/lib/api-client";
import { humanizeApiError } from "@/lib/form-errors";
import { useToast } from "@/components/ui/Toast";
import { useTopbar } from "@/components/chrome/use-topbar";
import { requireAdminRoute } from "@/lib/require-admin-route";
import { meProfileQueryOptions } from "@/lib/me-profile";
import { adminUsersQueryOptions, type AdminUser } from "@/lib/admin-users";
import { UsersTable } from "@/components/settings/UsersTable";
import { InviteForm } from "@/components/settings/InviteForm";
import { UserEditSheet } from "@/components/settings/UserEditSheet";

export const Route = createFileRoute("/_app/settings/users")({
  beforeLoad: requireAdminRoute,
  component: UsersPage,
});

function UsersPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: me } = useQuery(meProfileQueryOptions);
  const { data, isLoading } = useQuery(adminUsersQueryOptions);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);

  const toggleRole = useMutation({
    mutationFn: (u: AdminUser) =>
      apiFetch(`/api/admin/users/${u.userId}/role`, {
        method: "PUT",
        body: JSON.stringify({ role: u.role === "admin" ? "user" : "admin" }),
      }),
    onSuccess: () => {
      toast.success("Role updated.");
      void qc.invalidateQueries({ queryKey: ["admin-users"] });
      void qc.invalidateQueries({ queryKey: ["me", "profile"] });
    },
    onError: (err) => {
      if (err instanceof ApiRequestError && err.status === 400) {
        toast.error("You can't change your own role.");
        return;
      }
      toast.error(humanizeApiError(err).summary);
    },
  });

  const { setTopbar } = useTopbar();
  useEffect(() => {
    setTopbar({
      crumbs: [
        { label: "Operations" },
        { label: "Settings", to: "/settings" },
        { label: "Users & roles" },
      ],
      rightSlot: null,
    });
    return () => setTopbar({ crumbs: [], rightSlot: null });
  }, [setTopbar]);

  const users = data?.users ?? [];
  const activeCount = users.filter((u) => u.status === "active").length;
  const invitedCount = users.filter((u) => u.status === "invited").length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 border-b border-rule pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="mb-1 text-3xl font-semibold tracking-tight text-ink">Users &amp; roles</h1>
          <p className="text-sm text-muted">
            Manage who can access Switchback, their role, and signing eligibility.
            {!isLoading ? (
              <span className="ml-1 text-muted-2">
                {activeCount} active · {invitedCount} pending
              </span>
            ) : null}
          </p>
        </div>
        {!inviteOpen ? (
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="inline-flex items-center gap-2 self-start rounded-md bg-ink px-3.5 py-2 text-xs font-semibold text-white sm:self-auto"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            Invite user
          </button>
        ) : null}
      </div>

      {inviteOpen ? <InviteForm onCancel={() => setInviteOpen(false)} /> : null}

      {isLoading ? (
        <p className="text-sm text-muted">Loading users…</p>
      ) : (
        <UsersTable
          users={users}
          currentUserId={me?.userId}
          onEdit={(u) => setEditing(u)}
          onToggleRole={(u) => toggleRole.mutate(u)}
          roleChangingId={toggleRole.isPending ? (toggleRole.variables?.userId ?? null) : null}
        />
      )}

      {editing ? (
        <UserEditSheet
          user={editing}
          isSelf={!!editing.userId && editing.userId === me?.userId}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
